import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, MenuItem, Select } from '@mui/material';
import useDialog from 'hooks/useDialog';
import { ArchivedStatus } from '../ApplicationStatusControl';
import { ProfessionalArchiveReasonEnum } from 'typings/enums';
import { DateTimeInput, SimpleForm, Toolbar, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import ScreenerAccountInput from 'components/Filter/ScreenerAccountInput';

type ArchiveFormValues = {
  screener_account_id: number | null;
  scheduled_time: string | null;
};

type ProfessionalScreening = {
  id: number;
  screening_type: 'phone' | 'written';
  canceled_at: string | null;
  reason: 'application' | 're_screening';
  no_show: boolean;
  scheduled_time: string | null;
  screener_account_id: number | null;
};

type ArchivePayload = {
  application_status: typeof ArchivedStatus;
  archive_reason: string;
  professional_id?: number;
  screener_account_id?: number;
  scheduled_time?: string;
  reason?: string;
  screening_type?: 'phone' | 'written';
};

interface ArchiveItemProps {
  updateApplicationStatus: (val: ArchivePayload) => void;
}

const ArchiveItem = ({ updateApplicationStatus }: ArchiveItemProps) => {
  const { open, closeDialog, openDialog } = useDialog();

  return (
    <>
      <MenuItem onClick={openDialog}>Archive</MenuItem>
      <ArchiveDialog
        open={open}
        closeDialog={closeDialog}
        updateApplicationStatus={updateApplicationStatus}
      />
    </>
  );
};

const ArchiveToolBar = ({ closeDialog, archiveReason }) => {
  const { getValues } = useFormContext();
  const { screener_account_id, scheduled_time, screening_type } = getValues();
  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Button onClick={closeDialog}>Cancel</Button>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={
          !archiveReason || !screener_account_id || (!scheduled_time && screening_type === 'phone')
        }
      >
        Submit
      </Button>
    </Toolbar>
  );
};

const ArchiveDialog = ({ open, closeDialog, updateApplicationStatus }) => {
  const [archiveReason, setArchiveReason] = useState('');
  const record = useRecordContext();

  const latestScreening = record?.professional_screenings?.find(
    (screening: ProfessionalScreening) =>
      screening?.screening_type === 'phone' &&
      !screening?.canceled_at &&
      !screening?.no_show &&
      screening?.scheduled_time,
  );

  const defaultValues = {
    screener_account_id: latestScreening?.screener_account_id || null,
    scheduled_time: latestScreening?.scheduled_time || null,
  };

  const submitArchive = ({ screener_account_id, scheduled_time }: ArchiveFormValues) => {
    const hasScreeningChanges =
      screener_account_id !== defaultValues.screener_account_id ||
      scheduled_time !== defaultValues.scheduled_time;

    const payload: ArchivePayload = {
      application_status: ArchivedStatus,
      archive_reason: archiveReason,
    };

    if (hasScreeningChanges) {
      payload.professional_id = Number(record?.id);
      payload.screener_account_id = screener_account_id ?? undefined;
      payload.scheduled_time = scheduled_time ?? undefined;
      payload.reason = 'application';
      payload.screening_type = latestScreening?.screening_type || 'written';
    }

    updateApplicationStatus(payload);
  };

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>Archive Reason</DialogTitle>
      <SimpleForm
        key={open ? 'open' : 'closed'}
        onSubmit={submitArchive}
        toolbar={<ArchiveToolBar closeDialog={closeDialog} archiveReason={archiveReason} />}
        defaultValues={defaultValues}
      >
        <Select
          fullWidth
          value={archiveReason}
          label="Select Archive Reason"
          onChange={({ target }) => setArchiveReason(target.value as string)}
          required
          style={{ width: '550px', marginBottom: '20px' }}
        >
          {Object.keys(ProfessionalArchiveReasonEnum).map((reason) => (
            <MenuItem key={reason} value={ProfessionalArchiveReasonEnum[reason]}>
              {reason}
            </MenuItem>
          ))}
        </Select>
        <ScreenerAccountInput
          source="screener_account_id"
          label="Screening Recruiter"
          alwaysOn
          fullWidth
        />
        <DateTimeInput label="Screening Date And Time" source="scheduled_time" fullWidth />
      </SimpleForm>
    </Dialog>
  );
};

export default ArchiveItem;
